import { useEffect, useState, useRef } from "react";
import { CalendarIcon } from "lucide-react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

interface DialogDatePickerProps {
  value: Date;
  onChange: (date: Date) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
}

export function DialogDatePicker({ value, onChange, label, className = "", disabled = false }: DialogDatePickerProps) {
  const [dayInput, setDayInput] = useState<string>("");
  const [monthInput, setMonthInput] = useState<string>("");
  const [yearInput, setYearInput] = useState<string>("");
  const [dropdownDay, setDropdownDay] = useState<string>("");
  const [dropdownMonth, setDropdownMonth] = useState<string>("");
  const [dropdownYear, setDropdownYear] = useState<string>("");
  const [open, setOpen] = useState(false);

  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  // Update inputs when value changes
  useEffect(() => {
    const day = value.getDate().toString().padStart(2, '0');
    const month = (value.getMonth() + 1).toString().padStart(2, '0');
    const year = value.getFullYear().toString();

    // Only update state if the value clearly doesn't match roughly
    // This prevents cursor jumping and overwriting while typing e.g. "1" -> "01"
    if (day !== dayInput && day !== "0" + dayInput) {
      setDayInput(day);
    }
    if (month !== monthInput && month !== "0" + monthInput) {
      setMonthInput(month);
    }
    if (year !== yearInput) {
      setYearInput(year);
    }

    setDropdownDay(day);
    setDropdownMonth(month);
    setDropdownYear(year);
  }, [value]); // Remove dependencies on inputs to avoid loops, value triggers updates

  // Update date when inputs are complete
  const updateDateFromInputs = (day: string, month: string, year: string) => {
    // Year must be exactly 4 digits
    if (day.length >= 1 && month.length >= 1 && year.length === 4) {
      const dayNum = parseInt(day);
      const monthNum = parseInt(month);
      const yearNum = parseInt(year);

      if (dayNum >= 1 && dayNum <= 31 && monthNum >= 1 && monthNum <= 12 && yearNum >= 1900) {
        // Allow JavaScript to handle date rollover (e.g. 31 April -> 1 May)
        // This ensures an invalid typed date doesn't result in NO update (and fallback to old value)
        // Use 12:00 noon to avoid timezone issues that could shift the date by a day
        const newDate = new Date(yearNum, monthNum - 1, dayNum, 12, 0, 0, 0);

        // We only check if year matches to avoid wild jumps, but we trust day/month rollover
        if (newDate.getFullYear() === yearNum) {
          onChange(newDate);
        }
      }
    }
  };

  const handleDayBlur = () => {
    let d = dayInput;
    if (d.length === 1) {
      d = '0' + d;
      setDayInput(d);
    }
    updateDateFromInputs(d, monthInput, yearInput);
  };

  const handleYearBlur = () => {
    // Year must be exactly 4 digits - if not complete, restore from value
    if (yearInput.length !== 4) {
      const year = value.getFullYear().toString();
      setYearInput(year);
    } else {
      updateDateFromInputs(dayInput, monthInput, yearInput);
    }
  };

  const handleMonthBlur = () => {
    let m = monthInput;
    if (m.length === 1) {
      m = '0' + m;
      setMonthInput(m);
    }
    updateDateFromInputs(dayInput, m, yearInput);
  };

  // Handle day input
  const handleDayInput = (val: string) => {
    // Only allow numbers and max 2 digits
    const numVal = val.replace(/\D/g, '').slice(0, 2);

    // Limit to 31
    let finalVal = numVal;
    if (numVal.length === 2) {
      const num = parseInt(numVal);
      if (num > 31) {
        finalVal = '31';
      }
    }

    setDayInput(finalVal);

    // Auto-move to month when 2 digits entered
    if (finalVal.length === 2) {
      monthRef.current?.focus();
    }

    updateDateFromInputs(finalVal, monthInput, yearInput);
  };

  // Handle month input
  const handleMonthInput = (val: string) => {
    // Only allow numbers and max 2 digits
    const numVal = val.replace(/\D/g, '').slice(0, 2);

    // Limit to 12
    let finalVal = numVal;
    if (numVal.length === 2) {
      const num = parseInt(numVal);
      if (num > 12) {
        finalVal = '12';
      }
    }

    setMonthInput(finalVal);

    // Auto-move to year when 2 digits entered
    if (finalVal.length === 2) {
      yearRef.current?.focus();
    }

    updateDateFromInputs(dayInput, finalVal, yearInput);
  };

  // Handle year input
  const handleYearInput = (val: string) => {
    // Only allow numbers and max 4 digits
    const numVal = val.replace(/\D/g, '').slice(0, 4);
    setYearInput(numVal);
    // Only update date when exactly 4 digits are entered
    if (numVal.length === 4) {
      updateDateFromInputs(dayInput, monthInput, numVal);
    }
  };

  // Handle year focus - clear input for fresh entry
  const handleYearFocus = () => {
    // Clear the input
    setYearInput('');
    // Also select all content in case React state update is delayed
    setTimeout(() => {
      yearRef.current?.select();
    }, 10);
  };

  // Update date when dropdown changes
  const updateDateFromDropdown = (day: string, month: string, year: string) => {
    if (day && month && year) {
      const dayNum = parseInt(day);
      const monthNum = parseInt(month);
      const yearNum = parseInt(year);

      if (dayNum >= 1 && dayNum <= 31 && monthNum >= 1 && monthNum <= 12 && yearNum >= 1900) {
        // Use 12:00 noon to avoid timezone issues that could shift the date by a day
        const newDate = new Date(yearNum, monthNum - 1, dayNum, 12, 0, 0, 0);
        if (newDate.getDate() === dayNum && newDate.getMonth() === monthNum - 1) {
          onChange(newDate);
        }
      }
    }
  };

  const handleDropdownDayChange = (newDay: string) => {
    setDropdownDay(newDay);
    updateDateFromDropdown(newDay, dropdownMonth, dropdownYear);
  };

  const handleDropdownMonthChange = (newMonth: string) => {
    setDropdownMonth(newMonth);
    updateDateFromDropdown(dropdownDay, newMonth, dropdownYear);
  };

  const handleDropdownYearChange = (newYear: string) => {
    setDropdownYear(newYear);
    updateDateFromDropdown(dropdownDay, dropdownMonth, newYear);
  };

  // Generate arrays for dropdowns
  const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  const months = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 50 }, (_, i) => (currentYear - i).toString());

  return (
    <div className={cn("relative", className)}>
      {label && <Label className="text-xs text-muted-foreground mb-1.5 block">{label}</Label>}

      {/* Three separate inputs with Calendar Icon */}
      <div className="relative flex items-center gap-1">
        {/* Day Input */}
        <input
          ref={dayRef}
          type="text"
          value={dayInput}
          onChange={(e) => handleDayInput(e.target.value)}
          onBlur={handleDayBlur}
          onFocus={(e) => e.target.select()}
          placeholder="DD"
          disabled={disabled}
          className="w-12 h-9 px-2 text-sm text-center bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
          dir="ltr"
          maxLength={2}
        />

        <span className="text-muted-foreground">/</span>

        {/* Month Input */}
        <input
          ref={monthRef}
          type="text"
          value={monthInput}
          onChange={(e) => handleMonthInput(e.target.value)}
          onBlur={handleMonthBlur}
          onFocus={(e) => e.target.select()}
          placeholder="MM"
          disabled={disabled}
          className="w-12 h-9 px-2 text-sm text-center bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
          dir="ltr"
          maxLength={2}
        />

        <span className="text-muted-foreground">/</span>

        {/* Year Input */}
        <input
          ref={yearRef}
          type="text"
          value={yearInput}
          onChange={(e) => handleYearInput(e.target.value)}
          onBlur={handleYearBlur}
          onFocus={handleYearFocus}
          placeholder="YYYY"
          disabled={disabled}
          className="w-16 h-9 px-2 text-sm text-center bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
          dir="ltr"
          maxLength={4}
        />

        {/* Calendar Icon - Opens Popover */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled}
              className="h-9 w-9 hover:bg-muted"
              onClick={(e) => {
                e.preventDefault();
                setOpen(true);
              }}
            >
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-4" align="start" dir="rtl">
            <div className="space-y-2">
              <div className="text-sm font-medium mb-3">هەڵبژاردنی بەروار</div>
              {/* Three Dropdowns: Day / Month / Year */}
              <div className="flex gap-2" dir="ltr">
                {/* Day Dropdown */}
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground mb-1.5 block">ڕۆژ</Label>
                  <Select value={dropdownDay} onValueChange={handleDropdownDayChange}>
                    <SelectTrigger className="h-9 text-sm w-full">
                      <SelectValue placeholder="ڕۆژ" />
                    </SelectTrigger>
                    <SelectContent>
                      {days.map((d) => (
                        <SelectItem key={d} value={d}>
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Month Dropdown */}
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground mb-1.5 block">مانگ</Label>
                  <Select value={dropdownMonth} onValueChange={handleDropdownMonthChange}>
                    <SelectTrigger className="h-9 text-sm w-full">
                      <SelectValue placeholder="مانگ" />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Year Dropdown */}
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground mb-1.5 block">ساڵ</Label>
                  <Select value={dropdownYear} onValueChange={handleDropdownYearChange}>
                    <SelectTrigger className="h-9 text-sm w-full">
                      <SelectValue placeholder="ساڵ" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={y}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOpen(false)}
                >
                  داخستن
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
